import React, { useState, useRef } from 'react';
import { Box, IconButton, TextField, styled, useMediaQuery, useTheme } from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%', // Ensure container takes full width
  '& .MuiOutlinedInput-root': {
    position: 'relative',
    padding: '0 20px', // Padding for arrows on desktop
    width: '90px', // Fixed total width
    [theme.breakpoints.down('md')]: {
      padding: '0 !important', // No padding on mobile (no arrows)
      width: '100% !important', // Full width on mobile to match comments field
      minHeight: 'unset !important',
      height: 'auto !important',
    },
    '& input': {
      padding: '8px 4px', // Increased padding for better touch targets
      width: '50px', // Fixed width for input area
      textAlign: 'center',
      '-moz-appearance': 'textfield',
      [theme.breakpoints.down('md')]: {
        width: '100% !important', // Full width on mobile
        padding: '8px 4px !important', // Consistent padding on mobile
        height: 'auto !important',
        minHeight: 'unset !important',
      },
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0
      }
    }
  }
}));

const ArrowButton = styled(IconButton)(() => ({
  position: 'absolute',
  padding: 2,
  minWidth: 16,
  width: 16,
  height: '50%',
  opacity: 0,
  transition: 'opacity 0.2s',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)'
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.1rem'
  }
}));

const InputContainer = styled(Box)(() => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  '&:hover, &:focus-within': {
    '& .MuiIconButton-root': {
      opacity: 1
    }
  }
}));

const HoursInput = ({ value, onChange, disabled, max = 24, sx }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef(null);

  // Check for error state when value changes
  React.useEffect(() => {
    const numValue = parseFloat(localValue || '0');
    setHasError(!isNaN(numValue) && numValue > max);
  }, [localValue, max]);

  // Handle direct input changes
  const handleInputChange = (event) => {
    const newValue = event.target.value;
    
    // During editing, allow any valid numeric input
    if (isEditing) {
      if (newValue === '') {
        setLocalValue('');
        return;
      }
      
      // Only allow valid decimal numbers
      if (/^\d*\.?\d*$/.test(newValue)) {
        setLocalValue(newValue);
      }
      return;
    }

    // Not editing - validate and format
    const numValue = parseFloat(newValue);
    if (!isNaN(numValue) && numValue >= 0) {
      const formatted = numValue.toFixed(2);
      setLocalValue(formatted);
      onChange(formatted);
    }
  };

  // Handle blur to format the value
  const handleBlur = () => {
    setIsEditing(false);
    let numValue = parseFloat(localValue || '0');
    if (isNaN(numValue)) numValue = 0;
    const formatted = numValue.toFixed(2);
    setLocalValue(formatted);
    onChange(formatted);
  };

  // Handle focus to allow editing
  const handleFocus = (event) => {
    setIsEditing(true);
    // Fix selection issue by using requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      if (event.target && typeof event.target.select === 'function') {
        event.target.select();
      }
    });
  };

  // Handle increment/decrement
  const handleIncrement = (amount) => {
    const currentValue = parseFloat(localValue || '0');
    const newValue = Math.max(0, Math.min(max, currentValue + amount));
    const formatted = newValue.toFixed(2);
    setLocalValue(formatted);
    onChange(formatted);
  };

  // Handle keydown for custom deletion behavior
  const handleKeyDown = (event) => {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      const input = event.target;
      const cursorPosition = input.selectionStart;
      const currentValue = input.value;
      const decimalIndex = currentValue.indexOf('.');
      
      // Only handle deletion before decimal point
      if (cursorPosition <= decimalIndex) {
        // Allow normal deletion behavior
        const beforeDelete = currentValue.slice(0, event.key === 'Delete' ? cursorPosition : cursorPosition - 1);
        const afterDelete = currentValue.slice(event.key === 'Delete' ? cursorPosition + 1 : cursorPosition);
        const newValue = beforeDelete + afterDelete;
        
        // If it's a valid number or empty, allow it
        if (newValue === '' || /^\d*\.?\d*$/.test(newValue)) {
          setLocalValue(newValue || '');
          event.preventDefault();
          
          // Update cursor position
          const newPos = event.key === 'Delete' ? cursorPosition : cursorPosition - 1;
          setTimeout(() => input.setSelectionRange(newPos, newPos), 0);
        }
      }
    }
  };

  // On mobile, render a simple TextField to match comments field exactly
  if (isMobile) {
    return (
      <TextField
        inputRef={inputRef}
        type="text"
        value={localValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        size="small"
        variant="outlined"
        fullWidth
        error={hasError}
        helperText={hasError ? `Max ${max} hours` : ''}
        inputProps={{
          inputMode: 'decimal',
          'aria-label': 'hours input'
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: 'unset !important',
            height: 'auto !important',
            '& input': {
              padding: '8px 4px !important',
              fontSize: '0.75rem !important',
              height: 'auto !important',
              minHeight: 'unset !important',
              textAlign: 'center',
            }
          },
          ...sx
        }}
      />
    );
  }

  // Desktop version with arrows
  return (
    <InputContainer>
      <StyledTextField
        inputRef={inputRef}
        type="text"
        value={localValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        size="small"
        variant="outlined"
        error={hasError}
        helperText={hasError ? `Max ${max} hours` : ''}
        inputProps={{
          inputMode: 'decimal',
          'aria-label': 'hours input'
        }}
        InputProps={{
          startAdornment: (
            <Box sx={{
              position: 'absolute',
              left: 2,
              top: 0,
              bottom: 0,
              width: 16,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <ArrowButton
                size="small"
                onClick={() => handleIncrement(1)}
                disabled={disabled}
                tabIndex={-1}
                sx={{ top: 0 }}
              >
                <KeyboardArrowUp />
              </ArrowButton>
              <ArrowButton
                size="small"
                onClick={() => handleIncrement(-1)}
                disabled={disabled}
                tabIndex={-1}
                sx={{ bottom: 0 }}
              >
                <KeyboardArrowDown />
              </ArrowButton>
            </Box>
          ),
          endAdornment: (
            <Box sx={{
              position: 'absolute',
              right: 2,
              top: 0,
              bottom: 0,
              width: 16,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <ArrowButton
                size="small"
                onClick={() => handleIncrement(0.25)}
                disabled={disabled}
                tabIndex={-1}
                sx={{ top: 0 }}
              >
                <KeyboardArrowUp />
              </ArrowButton>
              <ArrowButton
                size="small"
                onClick={() => handleIncrement(-0.25)}
                disabled={disabled}
                tabIndex={-1}
                sx={{ bottom: 0 }}
              >
                <KeyboardArrowDown />
              </ArrowButton>
            </Box>
          )
        }}
        sx={sx}
      />
    </InputContainer>
  );
};

export default HoursInput;
